import * as z from "zod";
import { appInput, signedInDto } from "./auth";

// The second factors of the external users (ADR-024, ADR-151): TOTP, recovery codes and passkeys; the second step of a
// sign-in, the factors of "my account", and what Backstage does when a user lost them. The same schemas validate the
// routes of the gateways of the external users and the RPC methods of identity, but for a `…Body`, which identity gets
// with the app of the gateway (`…Input`, ADR-206).

/** How a sign-in can be completed after the password. A passkey is a whole sign-in by itself. */
export const mfaMethods = ["totp", "recovery_code", "passkey"] as const;
export type MfaMethod = (typeof mfaMethods)[number];

/** The second step must follow the password within this time; then the password is asked again. */
export const MFA_CHALLENGE_SECONDS = 5 * 60;
/**
 * A change to the factors needs a sign-in, or a password typed again, no older than this (ADR-024, point 5): a
 * session left open is not enough to add a factor of someone else, or to remove the user's.
 */
export const REAUTHENTICATION_SECONDS = 10 * 60;

/** The token identity gives for the second step: 256 bits, base64url. */
const challenge = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** Six digits, with the spaces an app shows between them. */
const totpCode = z
  .string()
  .max(20)
  .transform((code) => code.replace(/\s/g, ""));
/** As typed: identity takes away spaces, dashes and capitals. */
const recoveryCode = z.string().max(40);
const base64url = (max: number) =>
  z
    .string()
    .max(max)
    .regex(/^[A-Za-z0-9_-]*$/);
const password = z.string().max(512);

// --- The second step of a sign-in ---

/** Body of the second step in gateway-atelier: the challenge travels in a cookie, never in the body. */
export const totpCodeBody = z.object({ code: totpCode });
export const recoveryCodeBody = z.object({ code: recoveryCode });

export const verifyTotpInput = totpCodeBody.extend({ challenge });
export type VerifyTotpInput = z.input<typeof verifyTotpInput>;

export const useRecoveryCodeInput = recoveryCodeBody.extend({ challenge });
export type UseRecoveryCodeInput = z.input<typeof useRecoveryCodeInput>;

/** What a sign-in with a password says when a second factor is due: which ones the user can use. */
const mfaChallengeDto = z
  .object({ mfaRequired: z.literal(true), methods: z.array(z.enum(mfaMethods)) })
  .meta({ id: "MfaChallenge" });

/** What a right password leads to in gateway-atelier: the session, or the second step. */
export const signInOutcomeDto = z.union([signedInDto, mfaChallengeDto]);

// --- Passkeys (WebAuthn, `@simplewebauthn/browser` on the page) ---

const credentialDescriptor = z.object({
  id: base64url(1400),
  type: z.literal("public-key"),
  transports: z.array(z.string()).optional(),
});
const userVerification = z.enum(["required", "preferred", "discouraged"]);

/** `PublicKeyCredentialRequestOptionsJSON`: what the page hands to `navigator.credentials.get` for a sign-in. */
export const passkeyRequestOptionsDto = z
  .object({
    challenge: base64url(200),
    rpId: z.string(),
    timeout: z.number().int(),
    userVerification,
  })
  .meta({ id: "PasskeyRequestOptions" });
export type PasskeyRequestOptions = z.infer<typeof passkeyRequestOptionsDto>;

/** `PublicKeyCredentialCreationOptionsJSON`: what the page hands to `navigator.credentials.create`. */
export const passkeyCreationOptionsDto = z
  .object({
    challenge: base64url(200),
    rp: z.object({ id: z.string(), name: z.string() }),
    user: z.object({ id: base64url(200), name: z.string(), displayName: z.string() }),
    pubKeyCredParams: z.array(z.object({ alg: z.number().int(), type: z.literal("public-key") })),
    timeout: z.number().int(),
    excludeCredentials: z.array(credentialDescriptor),
    authenticatorSelection: z.object({
      residentKey: z.enum(["required", "preferred", "discouraged"]),
      requireResidentKey: z.boolean(),
      userVerification,
    }),
    attestation: z.literal("none"),
    extensions: z.object({ credProps: z.boolean() }),
  })
  .meta({ id: "PasskeyCreationOptions" });
export type PasskeyCreationOptions = z.infer<typeof passkeyCreationOptionsDto>;

const clientExtensionResults = z.record(z.string(), z.unknown());
const authenticatorAttachment = z.enum(["platform", "cross-platform"]).optional();

/** `RegistrationResponseJSON` of WebAuthn Level 3. */
export const passkeyRegistrationResponse = z.object({
  id: base64url(1400),
  rawId: base64url(1400),
  type: z.literal("public-key"),
  response: z.object({
    clientDataJSON: base64url(4000),
    attestationObject: base64url(16_000),
    authenticatorData: base64url(16_000).optional(),
    transports: z.array(z.string().max(20)).max(10).optional(),
    publicKeyAlgorithm: z.number().int().optional(),
    publicKey: base64url(2000).optional(),
  }),
  authenticatorAttachment,
  clientExtensionResults,
});

/** `AuthenticationResponseJSON` of WebAuthn Level 3. */
export const passkeyAuthenticationResponse = z.object({
  id: base64url(1400),
  rawId: base64url(1400),
  type: z.literal("public-key"),
  response: z.object({
    clientDataJSON: base64url(4000),
    authenticatorData: base64url(4000),
    signature: base64url(2000),
    userHandle: base64url(200).optional(),
  }),
  authenticatorAttachment,
  clientExtensionResults,
});

// a ceremony is for the relying party of one app (ADR-206): identity gets the app of the gateway with each call

export const beginPasskeyLoginInput = appInput;
export type BeginPasskeyLoginInput = z.input<typeof beginPasskeyLoginInput>;

/** Body of `/auth/passkey/login`. */
export const finishPasskeyLoginBody = z.object({ response: passkeyAuthenticationResponse });
export type FinishPasskeyLoginBody = z.input<typeof finishPasskeyLoginBody>;
export const finishPasskeyLoginInput = finishPasskeyLoginBody.extend(appInput.shape);
export type FinishPasskeyLoginInput = z.input<typeof finishPasskeyLoginInput>;

// --- The factors of the signed-in user ("my account", P6.3) ---

/** The password typed again, for a change to the factors (`REAUTHENTICATION_SECONDS`). */
export const reauthenticateInput = z.object({ password });
export type ReauthenticateInput = z.input<typeof reauthenticateInput>;

export const listFactorsInput = z.object({});
export type ListFactorsInput = z.input<typeof listFactorsInput>;

export const passkeyDto = z
  .object({
    id: z.uuid(),
    name: z.string().nullable(),
    /** Synced by a password manager or the platform's keychain, so on more than one device. */
    backedUp: z.boolean(),
    createdAt: z.iso.datetime(),
    lastUsedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "Passkey" });

/** The factors of the signed-in user: never a secret, a hash or a code. */
export const factorsDto = z
  .object({
    /** A role of the user requires a second factor (`party.role.requires_mfa`). */
    mfaRequired: z.boolean(),
    totp: z.object({ confirmedAt: z.iso.datetime() }).nullable(),
    passkeys: z.array(passkeyDto),
    recoveryCodesLeft: z.number().int(),
  })
  .meta({ id: "Factors" });
export type FactorsDto = z.infer<typeof factorsDto>;

/** The app names the issuer the authenticator app shows (ADR-206). */
export const enrollTotpInput = appInput;
export type EnrollTotpInput = z.input<typeof enrollTotpInput>;

/** Shown once: the QR code of `uri`, and the secret for whoever types it into the app. */
export const totpEnrollmentDto = z.object({ uri: z.string(), secret: z.string() }).meta({ id: "TotpEnrollment" });

export const confirmTotpInput = z.object({ code: totpCode });
export type ConfirmTotpInput = z.input<typeof confirmTotpInput>;

/** The recovery codes are new only with the first factor: shown once, never again. */
const recoveryCodes = z.array(z.string());
export const factorAddedDto = z
  .object({ recoveryCodes: recoveryCodes.nullable(), passkey: passkeyDto.optional() })
  .meta({ id: "FactorAdded" });

export const beginPasskeyRegistrationInput = appInput;
export type BeginPasskeyRegistrationInput = z.input<typeof beginPasskeyRegistrationInput>;

/** Body of `/auth/passkeys`. */
export const registerPasskeyBody = z.object({
  response: passkeyRegistrationResponse,
  /** A label of the user's: "MacBook", "YubiKey". */
  name: z.string().trim().min(1).max(100).optional(),
});
export type RegisterPasskeyBody = z.input<typeof registerPasskeyBody>;
export const registerPasskeyInput = registerPasskeyBody.extend(appInput.shape);
export type RegisterPasskeyInput = z.input<typeof registerPasskeyInput>;

export const removeFactorInput = z.discriminatedUnion("factor", [
  z.object({ factor: z.literal("totp") }),
  z.object({ factor: z.literal("passkey"), passkeyId: z.uuid() }),
]);
export type RemoveFactorInput = z.input<typeof removeFactorInput>;
export const factorRemovedDto = z.object({ removed: z.literal(true) });

export const regenerateRecoveryCodesInput = z.object({});
export type RegenerateRecoveryCodesInput = z.input<typeof regenerateRecoveryCodesInput>;
export const recoveryCodesDto = z.object({ recoveryCodes }).meta({ id: "RecoveryCodes" });

export const reauthenticatedDto = z.object({ reauthenticated: z.literal(true) });

// --- Backstage ---

/** The answer of the approval of a reset (ADR-201): every second factor of the user is gone. */
export const mfaResetDto = z.object({ reset: z.literal(true) });
